import React, { useCallback, useContext, useEffect, useState } from "react";
import InnerLayout from "../../../components/layouts/inner-layout";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import LoadingSpinner from "../../../components/helper-components/loading-spinner";
import moment from "moment";
import { DataContext } from "../../../components/layouts/main-layout";
import {
  goodsTableAction,
  goodsVoidAction,
} from "../../../redux/actions/purchase-action";
import { storeDetailsAction } from "../../../redux/actions/invoices-action";
import { formatIndianNumber } from "../../../components/helper-components/helper-function";
import DynamicTransactionTable from "../../../components/tables/dynamic-transaction-table";
import SalesPurchaseFilter from "../../../components/sales-section/sales-purchase-filter";
import BadgesComponent from "../../../components/tables/badges-component";
import TransSummaryComp from "../../../components/helper-components/trans-summary-comp";
import {
  ChartColumnIncreasing,
  FileText,
  MoveDownLeft,
  TimerIcon,
} from "lucide-react";

const GoodsTable = () => {
  const [billSummary, setBillSummary] = useState({});
  const [heading, setHeading] = useState([]);
  const { permissionFn } = useContext(DataContext);
  const dispatch = useDispatch();
  const [showFilter, setShowFilter] = useState(false);
  const [orders, setOrders] = useState([]);
  const [dataCount, setDataCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [action, setAction] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(null);
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [storeDetails, setStoreDetails] = useState({});
  const [reload, setReload] = useState(false);

  useEffect(() => {
    dispatch(storeDetailsAction(onSuccessStoreDetails, onErrorStoreDetails));
    const data = localStorage.getItem("ModPermission");
    if (data) {
      const parsed = JSON.parse(data);
      const grnData = parsed.find((obj) => obj?.GRN_list);
      if (grnData?.GRN_list) {
        setHeading(grnData.GRN_list);
      }
    }
  }, []);
  const onSuccessStoreDetails = (data) => {
    setStoreDetails(data?.data);
  };
  const onErrorStoreDetails = () => {};
  const [filterData, setFilterData] = useState({
    sort_by: "",
    sort_order: "",
    // selectColumn: "gort_invoice_number",
    selectColumn: "",
    from: "",
    to: "",
    inputValue: "",
    orderStatus: "",
  });

  useEffect(() => {
    dispatch(
      goodsTableAction(
        {
          pageNumber: currentPage,
          pageSize: pageSize,
          filterData,
        },
        onSuccess,
        onError
      )
    );
  }, [currentPage, pageSize, filterData, reload]);
  const onSuccess = (data) => {
    setError(false);
    setLoading(false);
    setOrders(data?.data?.orderList ?? []);
    setDataCount(data?.data?.totalCount);
    setBillSummary(data?.data?.orderStats);
  };
  const onError = (data) => {
    setError(true);
    setLoading(false);
  };
  const navigate = useNavigate();
  const paginationProps = {
    pageSize: pageSize,
    setPageSize: setPageSize,
    dataCount: dataCount,
    setCurrentPage: setCurrentPage,
    currentPage: currentPage,
  };
  const initialFilter = {
    sort_by: "",
    sort_order: "",
    selectColumn: "gort_invoice_number",
    from: "",
    to: "",
    inputValue: "",
    orderStatus: "",
  };

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        action &&
        currentIndex != null &&
        !event.target.closest(".relative")
      ) {
        setAction(false);
        setCurrentIndex(null);
      }
    };
    document.addEventListener("click", handleOutsideClick);
    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, [action, currentIndex]);

  const tableBodyData = orders?.map((item) => {
    return {
      id: item?.gort_id,
      gort_invoice_number:
        (
          <span className="fw_500 cursor-pointer">
            {item?.gort_invoice_number ?? "--"}
          </span>
        ) ?? "--",
      created_date: item?.created_date
        ? moment(item?.created_date).format("DD MMM YYYY")
        : "--",
      update_date: item?.update_date
        ? moment(item?.update_date).format("DD MMM YYYY")
        : "--",
      created_user_name: item?.created_user_name ?? "--",
      updated_user_name: item?.updated_user_name ?? "--",
      order_status: <BadgesComponent status={item?.gort_order_status} />,

      cmr_name: item?.cmr_name ?? "--",
      gort_total_amount: "₹ " + item?.gort_total_amount,
      itemData: item?.itemData?.length > 0 ? item?.itemData : [],
      is_draft_order: item?.is_draft_order,
      cmr_id: item?.cmr_id ?? "--",
      gort_order_status: item?.gort_order_status ?? "",
      is_voidable: item?.is_gort_voidable ?? true,
      is_voided: item?.is_voided ?? false,
      store_code: item?.store_code ?? "--",
      store_name: item?.store_name ?? "--",
      store_type: item?.store_type ?? "--",
      firm_name: item?.firm_name ?? "--",
      cmr_code: item?.cmr_code ?? "--",
    };
  });

  const filterby = showFilter ? (
    <SalesPurchaseFilter
      filterData={filterData}
      setFilterData={setFilterData}
      setCurrentPage={setCurrentPage}
    />
  ) : null;
  const handleFilterClick = useCallback(() => {
    setShowFilter((prevShowFilter) => !prevShowFilter);
  }, []);
  const totalGRNs = billSummary?.totalGRNs ?? 0;
  const averageGRNValue = billSummary?.averageGRNValue ?? 0;
  const pendingGRNs = billSummary?.pendingGRNs ?? 0;
  const totalQuantityReceived = billSummary?.totalQuantityReceived ?? 0;

  const TransactionSummary = [
    {
      conClass:
        "bg-[var(--trans-stats-con-green-bg)] rounded-[12px] h-[5.3rem] w-[25%] p-3 flex justify-between",
      label: "Total Qty Received",
      value: totalQuantityReceived?.count ?? 0,
      desc: `₹${formatIndianNumber(
        totalQuantityReceived?.value || 0
      )} received`,
      descClass:
        "text-[length:var(--trans-stats-con-desc-fs)] font-[var(--trans-stats-con-desc-fw)] text-[var(--trans-stats-con-green-fg)]",
      icon: <MoveDownLeft size={16} color="var(--trans-stats-con-green-fg)" />,
      labelClass:
        "text-[length:var(--trans-stats-con-label-fs)] font-[var(--trans-stats-con-label-fw)] text-[var(--trans-stats-con-label-fg)]",
      valueClass:
        "text-[length:var(--trans-stats-con-value-fs)] font-[var(--trans-stats-con-value-fw)] text-[var(--trans-stats-con-value-fg)] mt-2",
    },
    {
      conClass:
        "bg-[var(--trans-stats-con-orange-bg)] rounded-[12px] h-[5.3rem] w-[25%] p-3 flex justify-between",
      label: "Avg. GRN Value",
      value: `₹ ${formatIndianNumber(averageGRNValue?.value || 0)}`,
      desc: `${averageGRNValue?.changePercentage < 0 ? "" : "+"}${
        averageGRNValue?.changePercentage
      }% vs last week`,
      descClass:
        "text-[length:var(--trans-stats-con-desc-fs)] font-[var(--trans-stats-con-desc-fw)] text-[var(--trans-stats-con-orange-fg)]",
      icon: (
        <ChartColumnIncreasing
          size={16}
          color="var(--trans-stats-con-orange-fg)"
        />
      ),
      labelClass:
        "text-[length:var(--trans-stats-con-label-fs)] font-[var(--trans-stats-con-label-fw)] text-[var(--trans-stats-con-label-fg)]",
      valueClass:
        "text-[length:var(--trans-stats-con-value-fs)] font-[var(--trans-stats-con-value-fw)] text-[var(--trans-stats-con-value-fg)] mt-2",
    },
    {
      conClass:
        "bg-[var(--trans-stats-con-red-bg)] rounded-[12px] h-[5.3rem] w-[25%] p-3 flex justify-between",
      label: "Pending GRNs ",
      value: pendingGRNs?.count || 0,
      desc: `₹${formatIndianNumber(pendingGRNs?.value || 0)} pending`,
      descClass:
        "text-[length:var(--trans-stats-con-desc-fs)] font-[var(--trans-stats-con-desc-fw)] text-[var(--trans-stats-con-red-fg)]",
      icon: <TimerIcon size={16} color="var(--trans-stats-con-red-fg)" />,
      labelClass:
        "text-[length:var(--trans-stats-con-label-fs)] font-[var(--trans-stats-con-label-fw)] text-[var(--trans-stats-con-label-fg)]",
      valueClass:
        "text-[length:var(--trans-stats-con-value-fs)] font-[var(--trans-stats-con-value-fw)] text-[var(--trans-stats-con-value-fg)] mt-2",
    },
    {
      conClass:
        "bg-[var(--trans-stats-con-blue-bg)] rounded-[12px] h-[5.3rem] w-[25%] p-3 flex justify-between",
      label: "Total GRNs",
      value: totalGRNs?.count ?? 0,
      desc: `${totalGRNs?.changePercentage < 0 ? "" : "+"}${
        totalGRNs?.changePercentage
      }% vs yesterday`,
      descClass:
        "text-[length:var(--trans-stats-con-desc-fs)] font-[var(--trans-stats-con-desc-fw)] text-[var(--trans-stats-con-blue-fg)]",
      icon: <FileText size={16} color="var(--trans-stats-con-blue-fg)" />,
      labelClass:
        "text-[length:var(--trans-stats-con-label-fs)] font-[var(--trans-stats-con-label-fw)] text-[var(--trans-stats-con-label-fg)]",
      valueClass:
        "text-[length:var(--trans-stats-con-value-fs)] font-[var(--trans-stats-con-value-fw)] text-[var(--trans-stats-con-value-fg)] mt-2",
    },
  ];

  const summaryArray =
    TransactionSummary?.length > 0 ? (
      <div className="px-[24px] pt-[16px] flex gap-5">
        {TransactionSummary?.map((sum) => (
          <TransSummaryComp sum={sum} />
        ))}
      </div>
    ) : (
      <></>
    );

  return (
    <InnerLayout
      permissionFn={permissionFn}
      module="invoice"
      list={true}
      title="Goods Receipt Note"
    >
      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <></>
      ) : (
        <>
          <DynamicTransactionTable
            extraFilters={[
              { label: "Item Code", value: "item_code", column: true },
              { label: "Item Name", value: "item_name", column: true }
            ]}
            transaction="Goods Receipt"
            transaction_type="goods_receipt_note"
            setInvoiceItems={setInvoiceItems}
            invoiceItems={invoiceItems}
            options={options}
            orders={orders}
            setOrders={setOrders}
            tableHeading1={tableHeading1}
            is_checkbox_req={true}
            onClickItemFn={(id) => navigate(`/view-goods/${id}`)}
            list={true}
            onclkFn={() => {
              navigate(
                `/goods-receipt/${encodeURIComponent(
                  JSON.stringify([])
                )}/goods?new`
              );
            }}
            cloneFn={(id) => {
              navigate(
                `/goods-receipt/${encodeURIComponent(
                  JSON.stringify([])
                )}/goods?cloneID=${id}`
              );
            }}
            permissionFn={permissionFn}
            module={"goods_receipt_note"}
            path="goods"
            reload={reload}
            setReload={setReload}
            loading={loading}
            setLoading={setLoading}
            deleteAction={goodsVoidAction}
            label="Goods Receipt"
            showFilter={showFilter}
            filterFn={handleFilterClick}
            setFilterData={setFilterData}
            filterData={filterData}
            paginationProps={paginationProps}
            tableHeading={heading}
            tableBodyData={tableBodyData}
            placeholder="Search (/)"
            filterBy={filterby}
            initialFilter={{
              sort_by: "",
              sort_order: "",
              selectColumn: "gort_invoice_number",
              from: "",
              to: "",
              inputValue: "",
              orderStatus: "",
            }}
            handleSelect={(item) => {
              if (item?.value === "purchase_return") {
                navigate(
                  `/add-purchase-return/${encodeURIComponent(
                    JSON.stringify(invoiceItems)
                  )}/goods?type=gort&transfer=true`
                );
              } else {
                navigate(
                  `/add-purchase-invoice/${encodeURIComponent(
                    JSON.stringify(invoiceItems)
                  )}/goods?type=gort&transfer=true`
                );
              }
            }}
            actionsPermissions={{
              edit: ["draft"], // Edit allowed for "open" and "draft"
              delete: ["open", "draft"], // Delete allowed only for "open"
              print: ["open", "closed", "draft"], // Print allowed for "open" and "closed"
              send: ["open", "closed", "draft"], // Share allowed for "open" and "closed"
              clone: ["open", "draft", "closed"], // Clone allowed for "open", "draft" and "closed"
            }}
            summaryArray={summaryArray}
            fetchColumn={true}
          />
        </>
      )}
    </InnerLayout>
  );
};

export default GoodsTable;
const tableHeading = [
  {
    label: "GOODS RECEIPT NUMBER",
    value: "gort_invoice_number",
    display: true,
    column: true,
  },
  { label: "DATE", value: "update_date", display: true, column: false },
  {
    label: "ORDER STATUS",
    value: "order_status",
    display: false,
    column: false,
  },
  {
    label: "CUSTOMER NAME",
    value: "cmr_name",
    display: true,
    column: true,
  },
  {
    label: <span className="flex justify-end">AMOUNT</span>,
    value: "gort_total_amount",
    display: true,
    column: false,
    className: "justify-end pe-5",
  },
  { label: "", value: "", column: false },
];

const tableHeading1 = [
  { label: "PRODUCT NAME", value: "item_name" },
  { label: "GENERIC NAME", value: "item_generic_name" },
  { label: "ITEM CODE", value: "item_code" },
  { label: "UOM", value: "item_uom" },
  { label: "PRICE (₹)", value: "item_unit_price" },
  { label: "QTY", value: "item_quantity" },
  { label: "TOTAL (₹)", value: "item_total_amount" },
];

const options = [
  { value: "purchase_return", label: "Purchase Return" },
  { value: "purchase_invoice", label: "Purchase Invoice" },
];
